import { useState, useEffect, useRef, useContext } from "react";
import axios from "axios";
import Flatpickr from "react-flatpickr";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import moment from "moment";
import Header from "../layout/Header";
import Footer from "../layout/Footer";
import Loader from "../layout/Loader";
import Alert from "../Alert";
import { alertContext } from "../contexts/AlertProvider";
import PermissionGate from "../layout/PermissionGate";
import Pagination from "../layout/Pagination";
import normalize from "../utils/NormalizeSelect";

const ALL_ACTIONS_OPTION = { value: "all", label: "All Manual Actions" };
const EMAIL_SETTINGS_KEY = "manual-action-report-email";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ManualActionReport = () => {
    const [data, setData] = useState([]);
    const [actionTypes, setActionTypes] = useState([]);
    const [page, setPage] = useState(1);
    const [count, setCount] = useState(0);
    const [search, setSearch] = useState("");
    const [actionType, setActionType] = useState(ALL_ACTIONS_OPTION);
    const [isLoading, setIsLoading] = useState(true);
    const [start, setStart] = useState(
        moment().subtract(1, "months").startOf("day").format("YYYY-MM-DD"),
    );
    const [end, setEnd] = useState(moment().endOf("day").format("YYYY-MM-DD"));
    const dateRangePickerRef = useRef(null);
    const token = localStorage.getItem("token");
    const limit = process.env.REACT_APP_PAGINATION_LIMIT;
    const [, setAlertConfig] = useContext(alertContext);

    const [emailData, setEmailData] = useState({ enabled: false, recipients: [] });
    const [emailLoading, setEmailLoading] = useState(true);
    const [emailUpdating, setEmailUpdating] = useState(false);
    const [emailSending, setEmailSending] = useState(false);

    const fetchEmailSetting = () => {
        setEmailLoading(true);
        axios
            .get(
                `${process.env.REACT_APP_API_ENDPOINT}settings/get?key=${EMAIL_SETTINGS_KEY}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                },
            )
            .then((res) => {
                if (res.data.code === 1) {
                    const value = res?.data?.data?.value || {};
                    setEmailData({
                        enabled: value?.enabled ?? false,
                        recipients: Array.isArray(value?.recipients) ? value.recipients : [],
                    });
                }
            })
            .catch(console.error)
            .finally(() => setEmailLoading(false));
    };

    const handleRecipientsChange = (selected) => {
        const seen = new Set();
        const recipients = [];
        let hasInvalid = false;

        (selected ?? []).forEach((option) => {
            const email = (option?.value ?? "").trim().toLowerCase();
            if (!EMAIL_REGEX.test(email)) {
                hasInvalid = true;
                return;
            }
            if (seen.has(email)) return;
            seen.add(email);
            recipients.push(email);
        });

        if (hasInvalid) {
            setAlertConfig({
                show: true,
                message: "Enter a valid email address",
                type: "danger",
            });
        }

        setEmailData((prev) => ({ ...prev, recipients }));
    };

    const saveEmailSetting = async () => {
        if (emailData.enabled && emailData.recipients.length === 0) {
            setAlertConfig({
                show: true,
                message: "Add at least one recipient email",
                type: "danger",
            });
            return;
        }

        setEmailUpdating(true);
        try {
            const res = await axios.patch(
                `${process.env.REACT_APP_API_ENDPOINT}settings/patch?key=${EMAIL_SETTINGS_KEY}`,
                {
                    key: EMAIL_SETTINGS_KEY,
                    value: {
                        enabled: emailData.enabled,
                        recipients: emailData.recipients,
                    },
                },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            setAlertConfig({
                show: true,
                message: res.data.message,
                type: res.data.code === 1 ? "success" : "danger",
            });
        } catch (err) {
            console.error(err);
            setAlertConfig({
                show: true,
                message: "Something went wrong",
                type: "danger",
            });
        } finally {
            setEmailUpdating(false);
        }
    };

    const sendReportNow = async () => {
        setEmailSending(true);
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_API_ENDPOINT}order-log/manual-action-report-send-test`,
                {},
                { headers: { Authorization: `Bearer ${token}` } },
            );
            setAlertConfig({
                show: true,
                message: res.data.message,
                type: res.data.code === 1 ? "success" : "danger",
            });
        } catch (err) {
            console.error(err);
            setAlertConfig({
                show: true,
                message: "Something went wrong",
                type: "danger",
            });
        } finally {
            setEmailSending(false);
        }
    };

    const fetchActionTypes = () => {
        axios
            .get(`${process.env.REACT_APP_API_ENDPOINT}order-log/manual-action-types`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
            .then((res) => {
                if (res.data.code === 1) {
                    setActionTypes(res.data.data ?? []);
                }
            })
            .catch(console.error);
    };

    const fetchData = () => {
        setIsLoading(true);
        axios
            .get(
                `${process.env.REACT_APP_API_ENDPOINT}order-log/manual-action-report?startDate=${start}&endDate=${end}&page=${page}&limit=${limit}&search=${search}&actionType=${actionType?.value ?? "all"}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            )
            .then((res) => {
                if (res.data.code === 1) {
                    if (page > 1 && res.data.data.length === 0) {
                        setPage(page - 1);
                    } else {
                        setData(res.data.data);
                        setCount(res.data.count);
                        setIsLoading(false);
                    }
                } else {
                    setIsLoading(false);
                }
            })
            .catch(console.error);
    };

    const exportData = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_ENDPOINT}order-log/manual-action-report-export?startDate=${start}&endDate=${end}&search=${search}&actionType=${actionType?.value ?? "all"}`,
                {
                    responseType: "blob",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            const timestamp = Date.now();
            const filename = `manual_action_report_${timestamp}.xlsx`;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error("Export failed:", err);
        }
    };

    const openCalendar = () => {
        if (dateRangePickerRef.current) {
            dateRangePickerRef.current.flatpickr.open();
        }
    };

    const reset = () => {
        setPage(1);
        setSearch("");
        setActionType(ALL_ACTIONS_OPTION);
        const defaultStart = moment()
            .subtract(1, "months")
            .startOf("day")
            .format("YYYY-MM-DD");
        const defaultEnd = moment().endOf("day").format("YYYY-MM-DD");
        setStart(defaultStart);
        setEnd(defaultEnd);
    };

    useEffect(() => {
        fetchActionTypes();
        fetchEmailSetting();
    }, []);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, [start, end, page, search, actionType]);

    return (
        <div className="d-flex flex-column flex-root">
            <Alert />
            {/*begin::Page*/}
            <div className="page d-flex flex-row flex-column-fluid">
                {/*begin::Wrapper*/}
                <div
                    className="wrapper d-flex flex-column flex-row-fluid"
                    id="kt_wrapper"
                >
                    <Header />
                    <div className="app-body">
                        <div className="toolbar py-3 py-lg-6" id="kt_toolbar">
                            {/*begin::Container*/}
                            <div
                                id="kt_toolbar_container"
                                className=" container-xxl  d-flex flex-stack flex-wrap gap-2"
                            >
                                {/*begin::Page title*/}
                                <div className="page-title d-flex flex-column align-items-start me-3 py-2 py-lg-0 gap-2">
                                    {/*begin::Title*/}
                                    <h1 className="d-flex text-dark fw-bold m-0 fs-3">
                                        Manual Action Report
                                    </h1>
                                    {/*end::Title*/}
                                </div>
                                {/*end::Page title*/}
                            </div>
                            {/*end::Container*/}
                        </div>
                        {/*end::Toolbar*/}

                        {/*begin::Container*/}
                        <div
                            id="kt_content_container"
                            className="d-flex flex-column-fluid align-items-start  container-xxl "
                        >
                            {/*begin::Post*/}
                            <div className="content flex-row-fluid" id="kt_content">
                                {/*begin::Card*/}
                                <PermissionGate required={`view_manual_action_report`}>
                                    <div className="card">
                                        {/*begin::Card header*/}
                                        <div className="p-6">
                                            <div className="d-block d-md-flex justify-content-between gap-2">
                                                <div className="d-block d-md-flex gap-2">
                                                    <div className="mb-3">
                                                        <input
                                                            value={search}
                                                            onChange={(e) => {
                                                                setPage(1);
                                                                setSearch(e.target.value);
                                                            }}
                                                            type="text"
                                                            className="form-control w-100"
                                                            placeholder="Search Admin User"
                                                        />
                                                    </div>
                                                    <div className="mb-3" style={{ minWidth: "220px" }}>
                                                        <Select
                                                            className="react-select-styled react-select-bordered"
                                                            classNamePrefix="react-select"
                                                            value={actionType}
                                                            onChange={(e) => {
                                                                setPage(1);
                                                                setActionType(e ?? ALL_ACTIONS_OPTION);
                                                            }}
                                                            options={[
                                                                ALL_ACTIONS_OPTION,
                                                                ...actionTypes.map((type) => ({
                                                                    value: type.key,
                                                                    label: type.label,
                                                                })),
                                                            ]}
                                                            filterOption={(option, input) => {
                                                                return normalize(option.label).includes(
                                                                    normalize(input),
                                                                );
                                                            }}
                                                            placeholder="Action Type"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="d-block d-md-flex gap-2 flex-shrink-0">
                                                    {/* Date Range Picker */}
                                                    <div className="d-flex position-relative flex-shrink-0 mb-3">
                                                        <Flatpickr
                                                            ref={dateRangePickerRef}
                                                            value={[new Date(start), new Date(end)]}
                                                            options={{ dateFormat: "d-m-Y", mode: "range" }}
                                                            onChange={([start, end]) => {
                                                                if (start && end) {
                                                                    setPage(1);
                                                                    setStart(moment(start).format("YYYY-MM-DD"));
                                                                    setEnd(moment(end).format("YYYY-MM-DD"));
                                                                }
                                                            }}
                                                            className="form-control form-control-lg"
                                                            placeholder="Pick date"
                                                        />
                                                        <i
                                                            onClick={openCalendar}
                                                            className="bi-duotone bi-calendar fs-5 position-absolute"
                                                            style={{
                                                                top: "28%",
                                                                right: "5%",
                                                                cursor: "pointer",
                                                            }}
                                                        ></i>
                                                    </div>
                                                    {/* Reset Button */}
                                                    <div className="w-100 w-md-auto mb-3">
                                                        <button
                                                            onClick={reset}
                                                            className="btn btn-danger w-100"
                                                        >
                                                            <i className="ki-duotone ki-wrench fs-1">
                                                                <span className="path1"></span>
                                                                <span className="path2"></span>
                                                            </i>
                                                            Reset
                                                        </button>
                                                    </div>
                                                    {/* Export Button */}
                                                    <PermissionGate
                                                        required={`export_manual_action_report`}
                                                        fallback={<></>}
                                                        show={false}
                                                    >
                                                        <div className="w-100 w-md-auto">
                                                            <button
                                                                onClick={exportData}
                                                                className="btn btn-primary w-100"
                                                            >
                                                                <i className="ki-duotone ki-cloud-download fs-1">
                                                                    <span className="path1"></span>
                                                                    <span className="path2"></span>
                                                                </i>
                                                                Export
                                                            </button>
                                                        </div>
                                                    </PermissionGate>
                                                </div>
                                            </div>
                                        </div>

                                        {/*end::Card header*/}
                                        {/*begin::Card body*/}
                                        {isLoading === true ? (
                                            <Loader />
                                        ) : (
                                            <div className="card-body py-4 text-center">
                                                {/*begin::Table*/}
                                                {data?.length === 0 ? (
                                                    <div className="text-center w-100">
                                                        <img
                                                            className="w-25 mx-auto py-20"
                                                            src={`${process.env.REACT_APP_PUBLIC_URL}assets/media/misc/no-content.png`}
                                                            alt="no-content"
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="table-responsive">
                                                        <table
                                                            className="table align-middle table-row-dashed fs-6 gy-5"
                                                            id="kt_table_manual_action_report"
                                                        >
                                                            <thead>
                                                                <tr className="text-muted fw-bold fs-7 text-uppercase gs-0">
                                                                    <th className="text-center min-w-150px">
                                                                        Date &amp; Time
                                                                    </th>
                                                                    <th className="text-center min-w-120px">
                                                                        Admin User
                                                                    </th>
                                                                    <th className="text-center min-w-150px">
                                                                        Action
                                                                    </th>
                                                                    <th className="text-center min-w-120px">
                                                                        Order Code
                                                                    </th>
                                                                    <th className="text-center min-w-120px">
                                                                        Driver
                                                                    </th>
                                                                    <th className="text-center min-w-200px">
                                                                        Details
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="text-gray-600 fw-semibold">
                                                                {data &&
                                                                    data.map((log, index) => {
                                                                        const actionLabel =
                                                                            actionTypes.find(
                                                                                (type) => type.key === log?.actionType,
                                                                            )?.label ?? log?.details;
                                                                        return (
                                                                            <tr className="text-center" key={log?.orderLogId ?? index}>
                                                                                <td>
                                                                                    {log?.createdAt
                                                                                        ? moment(log.createdAt).format(
                                                                                            "DD-MM-YYYY hh:mm A",
                                                                                        )
                                                                                        : "---"}
                                                                                </td>
                                                                                <td>{log?.adminUsername ?? "---"}</td>
                                                                                <td>{actionLabel ?? "---"}</td>
                                                                                <td>{log?.orderCode ?? "---"}</td>
                                                                                <td>{log?.driverName ?? "---"}</td>
                                                                                <td>{log?.details ?? "---"}</td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}

                                                {/*end::Table*/}
                                                {/*begin::Paginate*/}
                                                {count && Math.ceil(count / limit) > 1 ? (
                                                    <Pagination
                                                        page={page}
                                                        setPage={setPage}
                                                        count={count}
                                                        limit={limit}
                                                    />
                                                ) : (
                                                    ""
                                                )}
                                                {/*end::Paginate*/}
                                            </div>
                                        )}
                                        {/*end::Card body*/}
                                    </div>
                                </PermissionGate>
                                {/*end::Card*/}

                                {/*begin::Email This Report Card*/}
                                <PermissionGate
                                    required={`view_manual_action_report_email_setting`}
                                    fallback={<></>}
                                    show={false}
                                >
                                    <div className="card mt-6">
                                        {/*begin::Card header*/}
                                        <div className="card-header">
                                            {/*begin::Card title*/}
                                            <div className="card-title">Email This Report (Daily)</div>
                                            {/*begin::Card title*/}
                                        </div>
                                        {/*end::Card header*/}

                                        {/*begin::Card body*/}
                                        {emailLoading === true ? (
                                            <Loader />
                                        ) : (
                                            <div className="card-body py-4">
                                                <div className="d-flex justify-content-between align-items-center mb-5">
                                                    <div>
                                                        <h5 className="mb-1 fw-bold">
                                                            Email a copy of this report every day
                                                        </h5>
                                                        <p className="text-muted mb-0">
                                                            Sends the manual action report for the day to
                                                            the recipients below, automatically at the end
                                                            of each day (Kuwait time).
                                                        </p>
                                                    </div>
                                                    <div className="form-check form-switch form-check-custom form-check-solid">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id="manualActionReportEmailEnabled"
                                                            checked={emailData.enabled}
                                                            onChange={(e) =>
                                                                setEmailData({
                                                                    ...emailData,
                                                                    enabled: e.target.checked,
                                                                })
                                                            }
                                                        />
                                                    </div>
                                                </div>

                                                {emailData.enabled && (
                                                    <div className="row">
                                                        <div className="col-12 mb-3 fv-row">
                                                            <label className="form-label required">
                                                                Recipient Email(s)
                                                            </label>
                                                            <CreatableSelect
                                                                className="react-select-styled react-select-bordered"
                                                                classNamePrefix="react-select"
                                                                isMulti
                                                                isClearable
                                                                value={emailData.recipients.map((email) => ({
                                                                    value: email,
                                                                    label: email,
                                                                }))}
                                                                onChange={handleRecipientsChange}
                                                                options={[]}
                                                                placeholder="Type an email and press enter"
                                                                formatCreateLabel={(input) => `Add "${input}"`}
                                                            />
                                                            <small className="text-muted">
                                                                Type an email address and press Enter to add
                                                                it. Add as many as needed.
                                                            </small>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {/*end::Card body*/}

                                        <PermissionGate
                                            required={`edit_manual_action_report_email_setting`}
                                            fallback={<></>}
                                        >
                                            <div className="card-footer d-flex gap-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-primary"
                                                    onClick={saveEmailSetting}
                                                    disabled={emailUpdating}
                                                    style={{ minWidth: "160px" }}
                                                >
                                                    {emailUpdating ? (
                                                        <>
                                                            Please wait...
                                                            <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                                        </>
                                                    ) : (
                                                        "Save Setting"
                                                    )}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-light-primary"
                                                    onClick={sendReportNow}
                                                    disabled={emailSending}
                                                    style={{ minWidth: "140px" }}
                                                >
                                                    {emailSending ? (
                                                        <>
                                                            Sending...
                                                            <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                                        </>
                                                    ) : (
                                                        "Send Now"
                                                    )}
                                                </button>
                                            </div>
                                        </PermissionGate>
                                    </div>
                                </PermissionGate>
                                {/*end::Email This Report Card*/}
                            </div>
                            {/*end::Post*/}
                        </div>
                    </div>
                    <Footer />
                </div>
                {/*end::Wrapper*/}
            </div>
            {/*end::Page*/}
        </div>
    );
};

export default ManualActionReport;